<script lang="ts" setup>
import Co2QuestionForm from '@/components/co2quiz/Co2QuestionForm.vue';
import Co2CalculatorForm from '@/components/co2quiz/Co2CalculatorForm.vue';
import Co2QuestionsSortingForm from '@/components/co2quiz/Co2QuestionsSortingForm.vue';
import Co2QuizOverview from '@/components/co2quiz/Co2QuizOverview.vue';
import ListFilter from '@/components/common/ListFilter.vue';
import TranslatedState from '@/components/common/TranslatedState.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import {
  Co2QuizQuestion,
  calculationLabelItems,
  getSimpleCalculationType,
} from '@/models/co2-quiz.model';
import { useEvents, Event } from '@/services/events.composable';
import { Mutations, ShiftCMSState } from '@/store';
import {
  Co2QuizStoreActions,
  Co2QuizStoreState,
} from '@/stores/co2-quiz.store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { Category } from '@/models/category.model';

const store = useStore<
  ShiftCMSState & { co2: Co2QuizStoreState; cs: CategoriesState }
>();
const events = useEvents();

const questions = computed<Co2QuizQuestion[]>(() => store.state.co2.questions);
const categories = computed<Category[]>(() => store.state.cs.categories);

const followQuestions = computed<number[]>(() =>
  questions.value.reduce((acc: number[], i: Co2QuizQuestion) => {
    const followUps: Co2QuizQuestion[] = i.answers
      .filter((a) => a.followUpQuestion)
      .map((a) => a.followUpQuestion)
      .filter((n) => !!n);
    return [...acc, ...followUps.map((f) => f.id)];
  }, []),
);

const relatedQuestions = computed<number[]>(() =>
  questions.value.reduce((acc: number[], i: Co2QuizQuestion) => {
    const related: Co2QuizQuestion[] = i.relatedQuestions;
    return [...acc, ...related.map((f) => f.id)];
  }, []),
);

const chainQuestions = computed<number[]>(() =>
  questions.value.reduce((acc: number[], i: Co2QuizQuestion) => {
    const chained: Co2QuizQuestion | undefined = i.chainQuestion;
    return chained ? [...acc, chained.id] : acc;
  }, []),
);

const update = () => {
  store.dispatch(Co2QuizStoreActions.GetCo2Questions);
  store.dispatch(CategoriesActions.GetCategories);
};

onMounted(() => {
  events.on(Event.LanguageChanged, update);
  update();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, update);
});

const headers = [
  { title: 'ID', key: 'id', sortable: false, width: '5%' },
  { title: 'Type', key: 'parentQuestion', width: '10%' },
  { title: 'Title', key: 'title', width: '50%' },
  { title: 'Category', key: 'categories', width: '15%' },
  { title: 'Calculation', key: 'calculationType', width: '20%' },
  { title: 'State', key: 'translated', sortable: false, width: '5%' },
];

const editQuestion = (_: Event, { item }: { item: Co2QuizQuestion }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2QuestionForm),
    data: item,
  });
};

const showCalculator = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2CalculatorForm),
    data: questions,
  });
};

const addQuestion = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2QuestionForm),
  });
};

const sortQuestions = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2QuestionsSortingForm),
    data: questions,
  });
};

const showQuizOverview = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(Co2QuizOverview),
    data: { questions, categories },
  });
};

const filterTitle = ref<string>('');
const filteredQuestions = computed(() =>
  questions.value.filter((v) =>
    filterTitle.value
      ? v.title.toLowerCase().indexOf(filterTitle.value.toLowerCase()) !== -1
      : true,
  ),
);

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filterTitle.value = filters.filterTitle;
}

const options = [
  { label: 'Main', value: 'main' },
  { label: 'Part', value: 'part' },
  { label: 'Sub', value: 'sub' },
  { label: 'Follow-up', value: 'follow-up' },
];
</script>
<template>
  <v-container>
    <h2>CO₂ Quiz Questions <CurrentLanguage /></h2>
    <ListFilter @filter="updateFilter" :options="options" />
    <v-data-table
      density="compact"
      :headers="headers"
      :items="filteredQuestions.sort((a, b) => a.index - b.index)"
      @click:row="editQuestion"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.translated="{ item }">
        <div style="display: flex; flex-direction: row">
          <TranslatedState :item="item" />
          &nbsp;
          <v-icon
            icon="mdi-tag-outline"
            color="info"
            v-if="item.calculationLabels.length"
            :title="item.calculationLabels.map(v => calculationLabelItems.find(l => l.value === v)).map(v => v!.title).join(', ')"
          ></v-icon>
        </div>
      </template>
      <template v-slot:item.calculationType="{ item }">
        <v-chip color="info" density="compact">{{
          getSimpleCalculationType(item)
        }}</v-chip>
      </template>
      <template v-slot:item.categories="{ item }">
        {{ item.categories[0]?.title }}
      </template>
      <template v-slot:item.parentQuestion="{ item }">
        <v-chip style="font-size: 0.7em" density="compact" color="success"
          >{{
            item.partQuestion
              ? 'Part'
              : item.parentQuestion
              ? 'Sub'
              : followQuestions.includes(item.id)
              ? 'Follow-up'
              : chainQuestions.includes(item.id)
              ? 'Chained'
              : item.isQuickScan
              ? 'Quick'
              : 'Other'
          }}
          {{ relatedQuestions.includes(item.id) ? '/ Related' : '' }}
        </v-chip>
      </template>
    </v-data-table>

    <v-btn @click="addQuestion" color="success" class="mr-4"
      >Add Question</v-btn
    >
    <v-btn @click="showCalculator" color="info" class="mr-4">Calculator</v-btn>
    <v-btn @click="sortQuestions" color="info" class="mr-4"
      >Sort and Hierarchy</v-btn
    >
    <v-btn @click="showQuizOverview" color="info">Quiz overview</v-btn>
  </v-container>
</template>
